import { useEffect, useState } from 'react';
import { apiUrl, authHeaders } from '@/lib/shell';

/** Server-owned rollout flags for knowledge pages and agent MCP access. */
export interface AgentFeatures {
  knowledgePages: boolean;
  knowledgeMcp: boolean;
  /** Storefront: the deployment has it and the signed-in account's company has access. */
  storefront: boolean;
  /** External MCP's workspace tools: deployed, and this company is in their rollout. */
  everywhere: boolean;
}

const disabled: AgentFeatures = {
  knowledgePages: false,
  knowledgeMcp: false,
  storefront: false,
  everywhere: false,
};

// Retries after an unreadable answer wait 1s, 2s, 4s … up to a minute, so a
// gateway error during a deploy is asked again soon without a focus storm.
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 60_000;

// The server's answer, once one was read, holds for the signed-in account and
// is read again when the page is focused, so a deploy or an operator turning
// Storefront on or off for the company shows without a reload. Another account
// reads the flags again (forgetAgentFeatures). Until the first answer every
// failure is transient; a failed re-read keeps the last answer.
let known: AgentFeatures | null = null;
let pending: Promise<AgentFeatures> | null = null;
let failures = 0;
let retryAt = 0;
// Bumped by the test reset, so an answer from before it is dropped.
let generation = 0;
const listeners = new Set<(value: AgentFeatures) => void>();

function parse(value: unknown): AgentFeatures {
  const flags = (value as { agentFeatures?: Record<string, unknown> } | null)?.agentFeatures;
  if (!flags || typeof flags !== 'object') return disabled;
  return Object.fromEntries(
    Object.keys(disabled).map((key) => [key, flags[key] === true]),
  ) as unknown as AgentFeatures;
}

/** Only a readable 2xx answer is the server's answer; anything else is retried. */
async function readConfig(): Promise<AgentFeatures> {
  const response = await fetch(apiUrl('/api/config'), {
    credentials: 'same-origin',
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error(`config_unavailable_${response.status}`);
  return parse(await response.json());
}

function settle(value: AgentFeatures) {
  for (const listener of [...listeners]) listener(value);
  return value;
}

/**
 * Fails closed: until the server's flags have been read, the features stay
 * hidden rather than invented. A failed read is never kept as the answer: the
 * next use or return to the page asks again, after a growing pause.
 */
export function loadAgentFeatures(): Promise<AgentFeatures> {
  if (known) return Promise.resolve(known);
  if (pending) return pending;
  if (Date.now() < retryAt) return Promise.resolve(disabled);
  const asked = generation;
  pending = readConfig().then(
    (value) => {
      if (asked !== generation) return value;
      known = value;
      failures = 0;
      retryAt = 0;
      pending = null;
      return settle(value);
    },
    () => {
      if (asked !== generation) return disabled;
      failures += 1;
      retryAt = Date.now() + Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1));
      pending = null;
      return settle(disabled);
    },
  );
  return pending;
}

/** Reads the flags again, keeping the last answer if the read fails. */
export function refreshAgentFeatures(): Promise<AgentFeatures> {
  if (!known) return loadAgentFeatures();
  if (pending) return pending;
  const asked = generation;
  pending = readConfig().then(
    (value) => {
      if (asked !== generation) return value;
      known = value;
      pending = null;
      return settle(value);
    },
    () => {
      if (asked === generation) pending = null;
      return known ?? disabled;
    },
  );
  return pending;
}

/** The flags belong to one account's company: forget them when another account signs in. */
export function forgetAgentFeatures() {
  generation += 1;
  known = null;
  pending = null;
  failures = 0;
  retryAt = 0;
}

export const resetAgentFeaturesForTests = forgetAgentFeatures;

/** `enabled` false asks nothing yet (the shell waits for the company, so an invitation is accepted first). */
export function useAgentFeatures(enabled = true): AgentFeatures | null {
  const [features, setFeatures] = useState<AgentFeatures | null>(known);
  useEffect(() => {
    // Until the company is ready, show what is known for it: nothing after another account.
    if (!enabled) {
      setFeatures(known);
      return;
    }
    let active = true;
    const update = (value: AgentFeatures) => {
      if (active) setFeatures(value);
    };
    listeners.add(update);
    void loadAgentFeatures().then(update);
    // Coming back to the page asks again: unread flags, or a change an operator made since.
    const onFocus = () => void refreshAgentFeatures();
    window.addEventListener('focus', onFocus);
    return () => {
      active = false;
      listeners.delete(update);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);
  return features;
}
